// ============================================================================
// Période du contrat — le salarié n'existe pas avant son embauche
// ============================================================================
// « J'embauche quelqu'un le 7 septembre, ça me le met dans l'EDT de cette
//   semaine. » La date de recrutement était saisie dans la fiche… et lue nulle
//   part. Le serveur ne planifie plus rien hors contrat ; les écrans, eux,
//   doivent cesser d'AFFICHER une ligne pour quelqu'un qui n'est pas encore là —
//   sinon il reste dans la grille, en repos toute la semaine, ce qui se lit
//   comme un salarié qui ne travaille jamais.
//
// Miroir de `employment-period.ts` côté serveur : bornes incluses des deux côtés.
// ============================================================================
import { format } from 'date-fns';

export interface ContractPeriod {
  hireDate?: string | null;
  /** Fin de contrat (CDD, remplaçant, intérim) ; absente pour un CDI. */
  endDate?: string | null;
}

/** Jour comparable « yyyy-MM-dd », que l'on parte d'un ISO serveur ou d'une Date. */
const dayOf = (d: Date | string): string =>
  typeof d === 'string' ? d.slice(0, 10) : format(d, 'yyyy-MM-dd');

/** Le salarié est-il sous contrat ce jour-là ? Embauche et fin INCLUSES. */
export function isEmployedOn(emp: ContractPeriod, date: Date | string): boolean {
  const d = dayOf(date);
  if (emp.hireDate && d < dayOf(emp.hireDate)) return false;
  if (emp.endDate && d > dayOf(emp.endDate)) return false;
  return true;
}

/** Le contrat croise-t-il la période affichée ? Sinon, pas de ligne du tout. */
export function isEmployedBetween(emp: ContractPeriod, from: Date | string, to: Date | string): boolean {
  if (emp.hireDate && dayOf(emp.hireDate) > dayOf(to)) return false;
  if (emp.endDate && dayOf(emp.endDate) < dayOf(from)) return false;
  return true;
}

/**
 * Ce qu'il faut dire d'une semaine que le contrat ne couvre qu'en partie :
 * « Arrive le 07/09 », « Contrat terminé le 30/09 ». `null` si la période est
 * entièrement couverte — dans ce cas il n'y a rien à signaler.
 */
export function contractNote(emp: ContractPeriod, from: Date, to: Date): string | null {
  const f = dayOf(from);
  const t = dayOf(to);
  const parts: string[] = [];
  if (emp.hireDate && dayOf(emp.hireDate) > f && dayOf(emp.hireDate) <= t) {
    parts.push(`arrive le ${format(new Date(emp.hireDate), 'dd/MM')}`);
  }
  if (emp.endDate && dayOf(emp.endDate) >= f && dayOf(emp.endDate) < t) {
    parts.push(`contrat jusqu'au ${format(new Date(emp.endDate), 'dd/MM')}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}
